<template>
    <div class="row g-3">

        <div class="col-md-6 col-xl-4" v-for="(item, itemIndex) in list" :key="itemIndex">
            <!--begin::Card-->
            <div class="card module-card border border-2 border-gray-300 h-100"
                 :class="cardClass(item)">
                <span v-if="statusBadge(item)"
                      class="module-card-badge"
                      :class="'module-card-badge--' + statusBadge(item).variant">
                    <i :class="['bi', statusBadge(item).icon]"></i>
                    {{ statusBadge(item).label }}
                </span>
                <!--begin:: Card body-->
                <component
                    :is="resolveTag(item)"
                    v-bind="resolveBindings(item)"
                    @click="handleCardClick($event, item)"
                >
                    <div class="card-body">
                        <!--begin::Card Title-->
                        <div class="card-title d-flex align-items-center gap-3 m-0">
                            <!--begin::Avatar-->
                            <div class="card-list-icon-container" :class="item.class ?? 'bg-light'">
                                    <span v-if="item.svgIcon || item.fontIcon" class="menu-icon">
                                        <span v-if="asideMenuIcons === 'svg'" class="svg-icon svg-icon-2">
                                            <inline-svg :src="item.svgIcon"/>
                                        </span>
                                    </span>
                            </div>
                            <!--end::Avatar-->
                            <!--begin::Name-->
                            <div class="fs-3 fw-bolder card-list-title" :class="item.class ?? ''">{{ item.name }}</div>
                            <!--end::Name-->
                        </div>
                        <!--end::Car Title-->
                        <!--begin::Description-->
                        <p class="text-gray-400 fw-bold fs-5 mt-4">{{ item.description }}</p>
                        <!--end::Description-->
                    </div>
                </component>
                <div class="card-footer p-5"
                     v-if="showFooter(item)"
                     :class="{ 'module-card-footer-info': statusBadge(item) }">
                    <template v-if="item.status_id === STATUS.INACTIVE">
                        <div class="module-card-info module-card-info--muted">
                            <i class="bi bi-pause-circle"></i>
                            <span>{{ $t('pages.module.statusInfo.inactive') }}</span>
                        </div>
                    </template>
                    <template v-else-if="item.status_id === STATUS.EXPIRED">
                        <div class="module-card-info module-card-info--warn">
                            <i class="bi bi-clock-history"></i>
                            <span>{{ $t('pages.module.statusInfo.expired') }}</span>
                        </div>
                    </template>
                    <template v-else>
                        <router-link :to="'/module/' + item.id + '/settings'" class="btn btn-flex btn-primary btn-sm" v-if="item.supported.settings">
                            <i className="bi bi-gear"></i>
                            {{ $t('common.settings') }}
                        </router-link>

                        <router-link :to="'/module/' + item.id + '/credentials'" class="btn btn-flex btn-primary btn-sm" v-if="item.supported.credentials">
                            <i className="bi bi-code-slash"></i>
                            {{ $t('common.credentials') }}
                        </router-link>
                    </template>
                </div>
            </div>

            <!--end:: Card body-->
            <!--end::Card-->
        </div>

    </div>
</template>

<script>
import {asideMenuIcons} from "@/core/helpers/config";
import panelManager from "@/core/helpers/panelManager";

const STATUS = {
    ACTIVE: 1,
    INACTIVE: 2,
    EXPIRED: 3,
};

export default {
    name: "module-list",
    props: {
        list: {type: Array},
    },
    data() {
        return {
            asideMenuIcons,
            STATUS,
        }
    },
    methods: {
        isInactive(item) {
            return item.status_id === STATUS.INACTIVE;
        },
        isExpired(item) {
            return item.status_id === STATUS.EXPIRED;
        },
        cardClass(item) {
            if (this.isInactive(item)) return 'module-card--inactive';
            if (this.isExpired(item)) return 'module-card--expired';
            return 'border-hover';
        },
        statusBadge(item) {
            if (this.isInactive(item)) {
                return { variant: 'muted', icon: 'bi-pause-circle', label: this.$t('pages.module.status.inactive') };
            }
            if (this.isExpired(item)) {
                return { variant: 'warn', icon: 'bi-clock-history', label: this.$t('pages.module.status.expired') };
            }
            return null;
        },
        showFooter(item) {
            if (this.isInactive(item) || this.isExpired(item)) return true;
            return item.supported?.settings || item.supported?.credentials;
        },
        resolveTag(item) {
            if (this.isInactive(item) || this.isExpired(item)) return 'a';
            if (item.action) return 'a';
            return (item.supported && item.supported.gui) ? 'router-link' : 'span';
        },
        resolveBindings(item) {
            if (this.isInactive(item) || this.isExpired(item)) return { href: '#' };
            if (item.action) return { href: '#' };
            return (item.supported && item.supported.gui) ? { to: item.slug ?? '' } : {};
        },
        handleCardClick(event, item) {
            if (this.isInactive(item)) {
                event.preventDefault();
                return;
            }
            if (this.isExpired(item)) {
                event.preventDefault();
                this.eventBus.$emit('showConfirmDialog', {
                    title: this.$t('pages.module.expiredDialog.title'),
                    message: this.$t('pages.module.expiredDialog.message'),
                    confirmText: this.$t('pages.module.expiredDialog.ok'),
                    confirmType: 'warning',
                    singleButton: true,
                });
                return;
            }
            if (item.action) {
                event.preventDefault();
                if (item.action.startsWith('panel:')) {
                    panelManager.openPanel(item.action.slice(6));
                } else {
                    this.eventBus.$emit(item.action);
                }
            }
        },
    },
}
</script>

<style scoped>
.module-card {
    position: relative;
    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.2s ease;
}
.module-card.border-hover:hover {
    border-color: var(--bs-primary) !important;
}

.module-card--inactive {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--bs-gray-100);
}
.module-card--inactive .card-list-icon-container {
    filter: grayscale(1);
}
.module-card--inactive a,
.module-card--inactive .card-body {
    pointer-events: none;
}

.module-card--expired {
    border-top: 3px solid var(--bs-warning) !important;
    opacity: 0.92;
}
.module-card--expired:hover {
    border-color: var(--bs-warning) !important;
}
.module-card--expired .card-body {
    cursor: help;
}

.module-card-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    z-index: 2;
    pointer-events: none;
}
.module-card-badge .bi {
    font-size: 12px;
}
.module-card-badge--muted {
    background: var(--bs-gray-200);
    color: var(--bs-gray-700);
}
.module-card-badge--warn {
    background: rgba(var(--bs-warning-rgb, 255, 199, 0), 0.18);
    color: #B85C00;
}

.module-card-footer-info {
    background: transparent;
    border-top: 1px dashed var(--bs-gray-300);
}
.module-card-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    line-height: 1.4;
    width: 100%;
}
.module-card-info .bi {
    font-size: 16px;
    flex-shrink: 0;
}
.module-card-info--muted {
    color: var(--bs-gray-600);
}
.module-card-info--warn {
    color: #B85C00;
}

.card-footer {
    display: flex;
    justify-content: flex-start;
}

.card-footer .btn {
    flex: 1 1 135px;
    max-width: 150px;
    justify-content: center;
    align-items: center;
}

</style>
